import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { ControlledTextField } from 'components/Form';
import { ModalDialog } from 'components/ModalDialog';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { Grid, GridItem } from 'uikit';
import { destroyAgGridTableFilters } from 'utils';

import type { GridApi } from 'ag-grid-community';
import * as R from 'ramda';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import type { IDataEditModal } from '../CatalogTypesWork';
import { useCreateTypeWork } from '../hooks/useCreateTypeWork';
import { useEditWorkTypeItem } from '../hooks/useEditWorkTypeItem';
import { useIsUniqueCodeTypeWork } from '../hooks/useIsUniqueCodeTypeWork';

export const AddOrEditTypeWorkModal = ({
    isModalOpened,
    closeHandler,
    isCreating = false,
    dataEditModal,
    idCatalog,
    refreshData,
    handlerWorkTypesUpdatedDate,
    gridRefApi,
    setFilterValues,
}: {
    isModalOpened: boolean;
    closeHandler: (state: boolean) => void;
    isCreating?: boolean;
    dataEditModal: IDataEditModal | null;
    idCatalog: number;
    refreshData: () => void;
    handlerWorkTypesUpdatedDate: () => void;
    gridRefApi: GridApi | undefined;
    setFilterValues: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;
}) => {
    const dispatch = useDispatch();

    const formRef = useRef<HTMLFormElement>(null);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const { control, handleSubmit, reset, setError, clearErrors, formState } = useForm<any>({
        criteriaMode: 'all',
    });

    const watchCode = useWatch({
        control,
        name: 'code',
    });

    const isUniqueCode = useIsUniqueCodeTypeWork({
        code: watchCode,
        catalogWorkTypeId: idCatalog,
    });

    useEffect(() => {
        if (watchCode && watchCode !== dataEditModal?.code && !isUniqueCode) {
            setError('code', {
                message: 'Такой вид работ уже существует',
            });
        } else {
            clearErrors('code');
        }
    }, [clearErrors, dataEditModal?.code, isUniqueCode, setError, watchCode]);

    const onSuccessHandler = useCallback(() => {
        refreshData();
        handlerWorkTypesUpdatedDate();
        destroyAgGridTableFilters({
            api: gridRefApi,
            columns: ['code', 'name'],
        });
        setFilterValues({});
    }, [gridRefApi, handlerWorkTypesUpdatedDate, refreshData, setFilterValues]);

    const {
        resetEditData,
        typeWorkEditData,
        typeWorkEditStatus,
        typeWorkEditError,
        typeWorkEditRequest,
        isSavingInProgress,
    } = useEditWorkTypeItem({
        onSuccessHandler,
    });

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: () => closeHandler?.(false),
        });

    const handleCloseModal = useCallback(() => {
        if (formState?.isDirty) {
            handleOpenConfirmationDialog();
        } else {
            closeHandler?.(false);
        }
    }, [closeHandler, formState?.isDirty, handleOpenConfirmationDialog]);

    const {
        createTypeWorkStatus,
        createTypeWorkError,
        createTypeWorkRequest,
        isCreatingInProgress,
        createTypeWorkData,
        resetCreatingData,
    } = useCreateTypeWork({
        onSuccessHandler,
    });

    const createOrEditStatus = useMemo(
        () => (isCreating ? createTypeWorkStatus : typeWorkEditStatus),
        [createTypeWorkStatus, isCreating, typeWorkEditStatus],
    );

    const createOrEditError = useMemo(
        () => (isCreating ? createTypeWorkError : typeWorkEditError),
        [createTypeWorkError, isCreating, typeWorkEditError],
    );

    const isCommit = useMemo(
        () => isCreatingInProgress || isSavingInProgress,
        [isCreatingInProgress, isSavingInProgress],
    );

    const createOrEditData = useMemo(
        () => (isCreating ? createTypeWorkData : typeWorkEditData),
        [createTypeWorkData, isCreating, typeWorkEditData],
    );

    const onSubmit = useCallback(
        (data) => {
            const variables = {
                code: data?.code,
                name: data?.name,
                unit: data?.unit,
            };

            const isEqual = dataEditModal
                ? R.equals(
                      R.props(['code', 'name', 'unit'], variables),
                      R.props(['code', 'name', 'unit'], dataEditModal),
                  )
                : false;

            if (isCreating) {
                dispatch(
                    createTypeWorkRequest({
                        input: {
                            catalogId: idCatalog,
                            ...variables,
                        },
                    }),
                );
            } else if (isEqual) {
                closeHandler?.(false);
            } else {
                dispatch(
                    typeWorkEditRequest({
                        input: {
                            catalogId: idCatalog,
                            id: dataEditModal?.id as number,
                            ...variables,
                        },
                    }),
                );
            }

            setIsSubmitted(true);
        },
        [closeHandler, createTypeWorkRequest, dataEditModal, dispatch, idCatalog, isCreating, typeWorkEditRequest],
    );

    const handleSave = useCallback(() => {
        formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }, [formRef]);

    useEffect(() => {
        if (isSubmitted && createOrEditData?.result) {
            resetCreatingData(null);
            resetEditData();
            closeHandler?.(false);
        }
    }, [closeHandler, createOrEditData?.result, isSubmitted, resetCreatingData, resetEditData]);

    const title = useMemo(() => (isCreating ? 'Создание вида работ' : 'Редактирование вида работ'), [isCreating]);
    const btnConfirmLabel = useMemo(() => (isCreating ? 'Создать' : 'Сохранить'), [isCreating]);

    const onCloseSaveErrorModal = useCallback(() => {
        setIsSubmitted(false);
    }, []);

    useEffect(() => {
        if (!isCreating) {
            reset({
                code: dataEditModal?.code,
                name: dataEditModal?.name,
                unit: dataEditModal?.unit,
            });
        }
    }, [dataEditModal?.code, dataEditModal?.name, dataEditModal?.unit, isCreating, reset]);

    const btnConfirmDisabled = !formState.isValid || !formState?.isDirty;

    return (
        <ModalDialog
            isOpen={isModalOpened}
            title={title}
            onClose={handleCloseModal}
            onConfirm={handleSave}
            withTitleCloseButton
            style={{ maxWidth: '520px', width: '100%' }}
            isCommit={isCommit}
            isOnClickOutsideDisable
            btnConfirmLabel={btnConfirmLabel}
            btnConfirmDisabled={btnConfirmDisabled}
        >
            <form onSubmit={handleSubmit(onSubmit)} ref={formRef}>
                <Grid rowGap="s" cols="1" style={{ maxWidth: '90%', padding: '24px' }}>
                    <GridItem>
                        <ControlledTextField
                            name="code"
                            label="Шифр"
                            control={control}
                            type="number"
                            directionTooltip="rightCenter"
                            tooltipSize="m"
                            rules={{
                                required: 'Обязательное поле',
                                maxLength: {
                                    value: 255,
                                    message: 'Максимальное количество символов – 255',
                                },
                            }}
                            required
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledTextField
                            name="name"
                            label="Наименование"
                            control={control}
                            type="text"
                            directionTooltip="rightCenter"
                            tooltipSize="m"
                            rules={{
                                required: 'Обязательное поле',
                                maxLength: {
                                    value: 255,
                                    message: 'Максимальное количество символов – 255',
                                },
                            }}
                            required
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledTextField
                            name="unit"
                            label="Ед.изм."
                            control={control}
                            type="text"
                            directionTooltip="rightCenter"
                            tooltipSize="m"
                            rules={{
                                required: 'Обязательное поле',
                                maxLength: {
                                    value: 255,
                                    message: 'Максимальное количество символов – 255',
                                },
                            }}
                            required
                        />
                    </GridItem>
                </Grid>
            </form>
            <ErrorHandlingModal
                titleText={title}
                customButtonText="Понятно"
                viewButton="primary"
                isSubmitted={isSubmitted}
                statusRequest={createOrEditStatus}
                dataRequest={createOrEditData}
                errorsRequest={createOrEditError}
                handleCloseModalError={onCloseSaveErrorModal}
                style={{ maxWidth: '520px' }}
            />
            <ConfirmationDialog
                textHeader="Отмена действия"
                textConfirmation="Вы уверены, что хотите отменить? Заполненные данные не будут сохранены"
                cancelButtonText="Не отменять"
                confirmButtonText="Отменить"
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={handleCloseConfirmationDialog}
            />
        </ModalDialog>
    );
};

AddOrEditTypeWorkModal.displayName = 'AddOrEditTypeWorkModal';
